import { Form } from '@unform/web';
import * as Yup from 'yup';
import Select from 'react-select';
import {
  Button,
  Container,
  Divider,
  FormLabel,
  Grid,
  IconButton,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { Create, Warning, Visibility, VisibilityOff } from '@material-ui/icons';
import { yellow } from '@material-ui/core/colors';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { useKeycloak } from '@react-keycloak/web';
import Loader from '../../components/Loader';
import Snackbar, { AlertComponentProps } from '../../components/Snackbar';

import { useAxios } from '../../utils/hooks';
import { Setting as SettingModel } from '../../models/setting';
import Input from '../../components/Input';
import getValidationErrors from '../../utils/getValidationErrors';
import { SelectProps } from '../Records/List/dto';
import { User } from '../../models/user';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    background: theme.palette.background.paper,
  },
  title: {
    color: theme.palette.secondary.light,
  },
  input: {
    display: 'none',
  },
  divider: {
    margin: theme.spacing(1),
    width: '100%',
  },
  img: {
    width: '100%',
    background: 'transparent',
  },
  warning: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.1rem',
  },
  formTitles: {
    fontWeight: 'bolder',
  },
  password: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  typogra: {
    margin: theme.spacing(2),
  },
  select: {
    paddingTop: theme.spacing(5),
  },
}));

const Settings: React.FC = () => {
  const api = useAxios();
  const classes = useStyles();
  const { keycloak } = useKeycloak();
  const formRef = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(true);
  const [showPass, setShowPass] = useState(false);
  const [openCreatePass, setOpenCreatePass] = useState(false);
  const [openUpdatePass, setOpenUpdatePass] = useState(false);
  const [hasSignature, setHasSignature] = useState<boolean | null>(null);
  const [hasSignaturePassword, setHasSignaturePassword] =
    useState<boolean | null>(null);
  const [users, setUsers] = useState<SelectProps[]>([]);
  const [setting, setSetting] = useState<SettingModel | null>(null);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  useEffect(() => {
    async function getSettings() {
      try {
        const settingResponse = await api.current?.get(`/users/settings`);

        setSetting(settingResponse?.data);

        const usersResponse = await api.current?.get('/users');

        if (usersResponse?.data) {
          setUsers(
            usersResponse.data.map((user: User) => ({
              value: user.id,
              label: `${user.firstName} ${user.lastName}`,
            })),
          );
        }
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao buscar configurações.',
        });
      } finally {
        setLoading(false);
      }
    }

    getSettings();
  }, [api]);

  // const handleSignature = async ({ target }: any) => {
  //   setLoading(true);
  //   const file = target.files[0];

  //   try {
  //     const formData = new FormData();
  //     formData.append('file', file);
  //     const fileResponse = await api.current?.post('/files', formData);

  //     if (fileResponse?.data) {
  //       const signatureResponse = await api.current?.patch('/users/signature', {
  //         file_id: fileResponse.data._id,
  //       });

  //       setSignature(signatureResponse?.data);
  //       setHasSignature(true);

  //       setAlert({
  //         isActive: true,
  //         type: 'success',
  //         message: 'Assinatura cadastrada com sucesso.',
  //       });
  //     }
  //   } catch (error) {
  //     setAlert({
  //       isActive: true,
  //       type: 'error',
  //       message: 'Erro ao cadastrar assinatura.',
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const toggleShowPass = () => {
    setShowPass(!showPass);
  };

  const handleOpenCreatePass = () => {
    setOpenCreatePass(true);
  };

  const handleCloseCreatePass = () => {
    setOpenCreatePass(false);
  };

  const handleOpenUpdatePass = () => {
    setOpenUpdatePass(true);
  };

  const handleCloseUpdatePass = () => {
    setOpenUpdatePass(false);
  };

  const resetAlert = () => {
    setAlert({
      isActive: false,
      type: 'info',
      message: 'none',
    });
  };

  const handleSupervisorSelect = useCallback(
    async (supervisor: any) => {
      setLoading(true);
      resetAlert();
      try {
        await api.current?.patch('users/settings', {
          ...setting,
          user_id: keycloak.subject,
          supervisor_id: supervisor
            ? supervisor.map((s: { value: string }) => s.value)
            : null,
        });

        const settingResponse = await api.current?.get('users/settings');

        if (settingResponse?.data) {
          setSetting(settingResponse.data);
        }

        setAlert({
          isActive: true,
          type: 'success',
          message: 'Supervisor atualizado com sucesso.',
        });
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao atualizar supervisor.',
        });
      } finally {
        setLoading(false);
      }
    },
    [api, setting, keycloak],
  );

  const handlePreceptorSelect = useCallback(
    async (preceptor: any) => {
      setLoading(true);
      resetAlert();
      try {
        await api.current?.patch('users/settings', {
          ...setting,
          user_id: keycloak.subject,
          preceptor_id: preceptor
            ? preceptor.map((p: { value: string }) => p.value)
            : null,
        });

        const settingResponse = await api.current?.get('users/settings');

        if (settingResponse?.data) {
          setSetting(settingResponse.data);
        }

        setAlert({
          isActive: true,
          type: 'success',
          message: 'Preceptor atualizado com sucesso.',
        });
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao atualizar preceptor.',
        });
      } finally {
        setLoading(false);
      }
    },
    [api, setting, keycloak],
  );

  return (
    <Container>
      <Loader loading={loading} />

      {/* <Dialog
        open={openCreatePass}
        onClose={handleCloseCreatePass}
        aria-labelledby="form-dialog-title"
      >
        <Form ref={formRef} onSubmit={handleSubmitCreatePass}>
          <DialogTitle id="form-dialog-title">Cadastrar Senha</DialogTitle>

          <DialogContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Insira a senha para assinar digitalmente
                </FormLabel>
                <br />
                <span>
                  (deve existir uma assinatura previamente cadastrada)
                </span>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="password"
                  label="Senha"
                  name="password"
                  size={4}
                  maxLength={4}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseCreatePass}>Cancelar</Button>
            <Button type="submit" color="primary" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </Form>
      </Dialog> */}

      {/* <Dialog
        open={openUpdatePass}
        onClose={handleCloseUpdatePass}
        aria-labelledby="form-dialog-title"
      >
        <Form ref={formRef} onSubmit={handleSubmitUpdatePass}>
          <DialogTitle id="form-dialog-title">Atualizar Senha</DialogTitle>

          <DialogContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Insira os dados para atualizar sua senha
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="old_password"
                  label="Senha atual"
                  name="old_password"
                  size={4}
                  maxLength={4}
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="new_password"
                  label="Nova senha"
                  name="new_password"
                  size={4}
                  maxLength={4}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseUpdatePass}>Cancelar</Button>
            <Button type="submit" color="primary" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </Form>
      </Dialog> */}

      <Grid
        container
        spacing={2}
        className={classes.root}
        style={{ display: loading ? 'none' : '' }}
      >
        <Grid item xs={12}>
          <h2 className={classes.title}>Configuração de conta</h2>
        </Grid>

        <Divider className={classes.divider} />

        <Grid
          container
          spacing={2}
          className={classes.root}
          style={{ display: loading ? 'none' : '' }}
        >
          <Grid item xs={5}>
            <Grid item xs={12}>
              <Typography
                align="center"
                variant="h6"
                component="h1"
                color="textSecondary"
              >
                Supervisor
              </Typography>
            </Grid>

            {!loading &&
              setting &&
              (!setting?.supervisor_id || !setting.supervisor_id.length) && (
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    component="h1"
                    color="textSecondary"
                    className={classes.warning}
                  >
                    <Warning style={{ color: yellow[800] }} />
                    Usuário não possui supervisor.
                  </Typography>
                </Grid>
              )}

            {setting?.supervisor_id && users.length && (
              <Select
                name="supervisor"
                isClearable
                className={classes.select}
                defaultValue={users.filter((user) =>
                  setting.supervisor_id.includes(user.value),
                )}
                isMulti
                options={users}
                onChange={handleSupervisorSelect}
              />
            )}

            {!setting?.supervisor_id && users.length && (
              <Select
                name="supervisor"
                isClearable
                className={classes.select}
                isMulti
                placeholder="Selecione um supervisor"
                options={users}
                onChange={handleSupervisorSelect}
              />
            )}
          </Grid>

          <Grid
            item
            xs={2}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <Divider orientation="vertical" />
          </Grid>

          <Grid item xs={5}>
            <Grid item xs={12}>
              <Typography
                align="center"
                variant="h6"
                component="h1"
                color="textSecondary"
              >
                Preceptor
              </Typography>

              {(!loading && setting && !setting?.preceptor_id) ||
                (!setting?.preceptor_id.length && (
                  <Grid item xs={12}>
                    <Typography
                      variant="h6"
                      component="h1"
                      color="textSecondary"
                      className={classes.warning}
                    >
                      <Warning style={{ color: yellow[800] }} />
                      Usuário não possui preceptor.
                    </Typography>
                  </Grid>
                ))}

              {setting?.preceptor_id && users.length && (
                <Select
                  name="preceptor"
                  isClearable
                  className={classes.select}
                  defaultValue={users.filter((user) =>
                    setting.preceptor_id.includes(user.value),
                  )}
                  isMulti
                  options={users}
                  onChange={handlePreceptorSelect}
                />
              )}

              {!setting?.preceptor_id && users.length && (
                <Select
                  name="preceptor"
                  isClearable
                  className={classes.select}
                  isMulti
                  placeholder="Selecione um preceptor"
                  options={users}
                  onChange={handlePreceptorSelect}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {alert.isActive && <Snackbar {...alert} />}
    </Container>
  );
};

export default Settings;
